import React, { useEffect, useMemo, useState } from 'react'
import {
  Route,
  Switch,
  useParams,
  useRouteMatch
} from 'react-router-dom'

import DashboardWrapper from 'components/DashboardWrapper'
import CardCollapse from 'components/CardCollapse'
import CoachingSessionCollabEditor from 'components/CoachingSessionCollab/CoachingSessionCollabEditor'
import { useCoachingSessionCollabDoc } from 'components/CoachingSessionCollab/useCoachingSessionCollabDoc'
import CollabSessionCollapseHeader from 'components/CoachingSessionCollab/CollabSessionCollapseHeader'
import PageLoader from 'components/PageLoader'
import { useSession } from 'pages/Member/CoachingSessions/hooks'
import { GET_SESSIONS } from 'pages/Member/CoachingSessions/constants'
import { queryClient } from 'store/state'

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Collaboration from '@tiptap/extension-collaboration'
import Underline from '@tiptap/extension-underline'
import LinkExtension from '@tiptap/extension-link'
import CollaborationCursor from '@tiptap/extension-collaboration-cursor'

const toSteps = session => {
  const raw = session?.actionSteps || session?.steps || []
  if (!Array.isArray(raw)) return []
  return raw.map((s, idx) => ({
    id: s?.id ?? idx + 1,
    description:
      typeof s?.description === 'string' ? s.description : '',
    status: !!s?.status
  }))
}

const CollabNotesReadOnly = ({
  ydoc,
  awareness,
  userIdentity
}) => {
  const editor = useEditor(
    {
      editable: false,
      extensions: [
        StarterKit.configure({ history: false }),
        Underline,
        LinkExtension.configure({
          openOnClick: true,
          autolink: true,
          linkOnPaste: true
        }),
        Collaboration.configure({
          document: ydoc,
          field: 'notes'
        }),
        ...(awareness
          ? [
              CollaborationCursor.configure({
                provider: { awareness },
                user: userIdentity || undefined
              })
            ]
          : [])
      ],
      editorProps: {
        attributes: {
          class: 'tiptap',
          style:
            'min-height: 120px; padding: 0; border: none; border-radius: 0; background: transparent; line-height: 1; font-family: inherit;'
        }
      }
    },
    [awareness, userIdentity, ydoc]
  )

  if (!editor) return null
  return <EditorContent editor={editor} />
}

const CollabSessionPanel = ({
  session,
  isEditing,
  onEdit,
  onDoneEditing,
  onSaveSteps
}) => {
  const {
    ydoc,
    awareness,
    ready,
    hasRemoteState,
    isSaving,
    lastRefresh,
    emitRefresh,
    userIdentity
  } = useCoachingSessionCollabDoc(Number(session?.id))

  useEffect(() => {
    if (!lastRefresh) return
    queryClient.invalidateQueries(GET_SESSIONS)
  }, [lastRefresh])

  if (!ready) {
    return (
      <PageLoader loader>
        <div />
      </PageLoader>
    )
  }

  if (isEditing) {
    return (
      <div>
        <CoachingSessionCollabEditor
          ydoc={ydoc}
          awareness={awareness}
          userIdentity={userIdentity}
          coachingSession={session}
          hasRemoteState={hasRemoteState}
          isSaving={isSaving}
          embedded
          onSaveSteps={steps => {
            if (!session?.id) return
            if (onSaveSteps)
              onSaveSteps(
                session.id,
                steps,
                session,
                emitRefresh
              )
          }}
        />
      </div>
    )
  }

  const steps = toSteps(session)
  return (
    <div>
      <CollabNotesReadOnly
        ydoc={ydoc}
        awareness={awareness}
        userIdentity={userIdentity}
      />

      <div className="mt-15">
        <div className="font-weight-bold mb-10">
          Action Steps
        </div>
        {steps.length === 0 ? (
          <div className="text-muted">No action steps.</div>
        ) : (
          steps.map(step => (
            <div
              key={`collab-step-${session?.id}-${step.id}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '28px 1fr',
                gap: 10,
                alignItems: 'center',
                marginBottom: 10
              }}
            >
              <input
                type="checkbox"
                checked={!!step.status}
                disabled
                readOnly
              />
              <div>
                {step.description || (
                  <span className="text-muted">(empty)</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

const CollabSessionsList = ({ sessions, onSaveSteps }) => {
  const { coachingSessionId } = useParams()
  const idFromUrl = Number(coachingSessionId)

  const orderedSessions = useMemo(() => {
    if (!Array.isArray(sessions)) return []
    return [...sessions].sort((a, b) => {
      const da = new Date(a?.date || a?.createdAt || 0).getTime()
      const db = new Date(b?.date || b?.createdAt || 0).getTime()
      return db - da
    })
  }, [sessions])

  const [openSessionIds, setOpenSessionIds] = useState(
    () => new Set()
  )
  const [editingSessionId, setEditingSessionId] = useState(null)

  // Default: newest expanded once sessions are loaded.
  useEffect(() => {
    if (!orderedSessions.length) return
    setOpenSessionIds(prev => {
      if (prev.size > 0) return prev
      const next = new Set()
      next.add(Number(orderedSessions[0]?.id))
      return next
    })
  }, [orderedSessions])

  // Optional route compatibility: auto-expand session id from URL.
  useEffect(() => {
    if (!idFromUrl || Number.isNaN(idFromUrl)) return
    setOpenSessionIds(prev => {
      const next = new Set(prev)
      next.add(idFromUrl)
      return next
    })
  }, [idFromUrl])

  // Prune open ids when the list changes.
  useEffect(() => {
    const valid = new Set(
      orderedSessions.map(s => Number(s?.id))
    )
    setOpenSessionIds(prev => {
      const next = new Set([...prev].filter(id => valid.has(id)))
      return next
    })
    setEditingSessionId(prev =>
      prev && valid.has(prev) ? prev : null
    )
  }, [orderedSessions])

  const toggleOpen = id => {
    setOpenSessionIds(prev => {
      const next = new Set(prev)
      const idNum = Number(id)
      if (next.has(idNum)) next.delete(idNum)
      else next.add(idNum)
      return next
    })
  }

  const toggleEditing = id => {
    const idNum = Number(id)
    if (!idNum || Number.isNaN(idNum)) return
    setEditingSessionId(prev =>
      Number(prev) === idNum ? null : idNum
    )
    setOpenSessionIds(prev => {
      const next = new Set(prev)
      next.add(idNum)
      return next
    })
  }

  return (
    <div style={{ padding: 24 }}>
      <div className="mt-20">
        {orderedSessions.length === 0 ? (
          <div className="text-muted">
            No coaching sessions yet.
          </div>
        ) : (
          orderedSessions.map(s => {
            const id = Number(s?.id)
            const isOpen = openSessionIds.has(id)
            const isEditing = Number(editingSessionId) === id
            return (
              <CardCollapse
                key={String(id)}
                isOpen={isOpen}
                className="mb-10 mb-md-20"
                disableOpenBorder
                headerComponent={
                  <CollabSessionCollapseHeader
                    title={s.title || 'Untitled'}
                    isOpen={isOpen}
                    date={s.date}
                    toggle={() => toggleOpen(id)}
                    isEditing={isEditing}
                    onToggleEditing={() => toggleEditing(id)}
                  />
                }
              >
                {isOpen ? (
                  <CollabSessionPanel
                    session={s}
                    isEditing={isEditing}
                    onEdit={() => toggleEditing(id)}
                    onDoneEditing={() =>
                      setEditingSessionId(null)
                    }
                    onSaveSteps={onSaveSteps}
                  />
                ) : null}
              </CardCollapse>
            )
          })
        )}
      </div>
    </div>
  )
}

const CoachingSessionsCollab = () => {
  const { path, url } = useRouteMatch()
  const { data: sessions, editSession } = useSession()

  return (
    <DashboardWrapper>
      <Switch>
        <Route path={`${path}/:coachingSessionId?`}>
          <CollabSessionsList
            sessions={sessions}
            onSaveSteps={(id, steps, current, emitRefresh) => {
              const payload = {
                title: current?.title || '',
                date: current?.date || '',
                agenda: Array.isArray(current?.agendas)
                  ? current.agendas
                  : [],
                notes:
                  typeof current?.notes === 'string'
                    ? current.notes
                    : '',
                steps
              }
              editSession(
                { id, payload },
                {
                  onSuccess: () => {
                    if (emitRefresh)
                      emitRefresh({
                        coachingSessionId: id,
                        reason: 'steps'
                      })
                  }
                }
              )
            }}
          />
        </Route>
      </Switch>
    </DashboardWrapper>
  )
}

export default CoachingSessionsCollab
